import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Printer, Award, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/apiClient";

export default function Certificate() {
  const { courseId } = useParams();
  const [cert, setCert] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/courses/${courseId}/certificate`).then((r) => setCert(r.data)).catch(() => setError(true));
  }, [courseId]);

  if (error) return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center">
      <h1 className="font-display text-2xl font-bold text-blue-950">Certificate not available</h1>
      <p className="mt-2 text-slate-600">Complete the course and pass the final quiz to unlock your certificate.</p>
      <Button asChild className="mt-6 bg-blue-900 text-white"><Link to={`/learn/${courseId}`}>Go to course</Link></Button>
    </div>
  );
  if (!cert) return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  const date = cert.completed_at ? new Date(cert.completed_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "";

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center justify-between mb-6 no-print">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-blue-800 hover:gap-2.5 transition-all"><ArrowLeft className="w-4 h-4" /> My Portal</Link>
        <Button onClick={() => window.print()} data-testid="print-certificate-button" className="bg-blue-900 hover:bg-blue-950 text-white gap-2"><Printer className="w-4 h-4" /> Print / Save PDF</Button>
      </div>

      <div data-testid="certificate" className="relative bg-white rounded-2xl shadow-xl border-[6px] border-blue-900 p-10 sm:p-16 text-center overflow-hidden">
        <div className="absolute inset-3 border border-amber-400/60 rounded-xl pointer-events-none" />
        <span className="block h-20 w-20 mx-auto mb-4 rounded-xl" style={{ backgroundImage: "url('/logo.png')", backgroundSize: "205% auto", backgroundPosition: "center 5%", backgroundRepeat: "no-repeat" }} />
        <p className="text-xs uppercase tracking-[0.28em] text-blue-700 font-semibold">T5 Care Academy</p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-blue-950 mt-4">Certificate of Completion</h1>
        <p className="mt-6 text-slate-500">This certifies that</p>
        <p className="font-display text-2xl sm:text-3xl font-bold text-amber-600 mt-2" data-testid="cert-name">{cert.name}</p>
        <p className="mt-5 text-slate-600 max-w-xl mx-auto leading-relaxed">has successfully completed the course</p>
        <p className="font-display text-xl sm:text-2xl font-semibold text-blue-900 mt-2" data-testid="cert-course">{cert.course}</p>
        {cert.ce_hours ? <p className="mt-3 inline-block bg-teal-50 text-teal-700 text-sm font-semibold px-4 py-1.5 rounded-full" data-testid="cert-ce-hours">{cert.ce_hours} Continuing Education (CE) Hours</p> : null}
        <div className="mt-10 flex items-center justify-center gap-12">
          <div className="text-center"><p className="font-display text-lg font-semibold text-blue-950">{date}</p><p className="text-xs uppercase tracking-wide text-slate-400 border-t border-slate-200 pt-1 mt-1">Date</p></div>
          <Award className="w-12 h-12 text-amber-500" />
          <div className="text-center"><p className="font-display text-lg font-semibold text-blue-950">T5 Care Service</p><p className="text-xs uppercase tracking-wide text-slate-400 border-t border-slate-200 pt-1 mt-1">Columbus, Ohio</p></div>
        </div>
        {cert.certificate_no && <p className="mt-8 text-xs text-slate-500" data-testid="cert-number">Certificate No. <span className="font-mono font-semibold text-blue-950">{cert.certificate_no}</span></p>}
        <p className="mt-2 text-[11px] text-slate-400">Ohio Department of Aging PASSPORT Approved Provider · Proudly Black-Owned & Minority-Owned</p>
      </div>
    </div>
  );
}
